// SCR-STAFF-705 — ارجاع‌ها (US-MED-061، 062، 066): ورودی (به من، تخصص یا شعبه من) با پذیرش، رد با دلیل، رزرو نوبت SCH پیش‌پر و پاسخ؛ خروجی با
// مشاهده پاسخ، بستن و لغو؛ پیگیری (med.referral.schedule) با وضعیت، مدت انتظار و فوریت و برجسته‌سازی Urgent بیش از ۲۴ ساعت (BR-MED-062).
// AP-MED-03 (US-MED-076): تب «کد رهگیری» — هر کد (ویزیت، نسخه، ارجاع، بازخورد، دستور بستری) مواجهه‌اش را پیدا
// می‌کند. این تب عملیاتی است و بی محتوای بالینی، پس با `med.national_referral.track` پذیرش هم آن را می‌بیند.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type MedReferralListItem } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, FormDialog, ListPage, LoadingState, ReasonDialog, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BookingDialog } from '../scheduling/BookingDialog';
import { InboundReferralsPanel } from './Phase2Panels';
import { TrackingCodeSearchPanel } from './referralNational';
import { medKeys, ReferralStatusChip, SCR_STAFF_705 } from './common';

type Box = 'incoming' | 'outgoing' | 'tracking' | 'inbound' | 'codes';

/**
 * ارجاع ملی (US-MED-065؛ AP-INT-04): ثبت با کد رهگیری ملی و دریافت بازخورد سطح بعد. ثبت ملی **افزوده** جریان ارجاع است
 * و وضعیت ارجاع داخلی را عوض نمی‌کند؛ سامانه در دسترس نبود ← علت نمایش داده می‌شود و کاربر دوباره تلاش می‌کند
 * (AC-MED-065.1). تا دریافت دسترسی رسمی (Q3) فقط Adapter نمونه پاسخ می‌دهد.
 */
function NationalReferralPanel({ id, canWrite }: { id: string; canWrite: boolean }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const key = [...medKeys.referral(session?.tenantId, id), 'national'];
  const national = useQuery({
    queryKey: key,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrals/{id}/national', { params: { path: { id } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const register = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referrals/{id}:register-national', {
          params: { path: { id } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      refresh();
    },
  });
  const feedback = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/medical-orders/referrals/{id}:fetch-national-feedback', { params: { path: { id } } })),
    onSettled: refresh,
  });
  const data = national.data;

  return (
    <Stack spacing={1} data-testid="med-national-referral">
      <Typography variant="subtitle2">{t('med:national_referral.title')}</Typography>
      {national.isPending ? (
        <LoadingState rows={1} />
      ) : national.error || !data ? (
        <ErrorState error={national.error} onRetry={() => void national.refetch()} compact />
      ) : (
        <Stack spacing={1}>
          {data.trackingCode ? (
            <Typography variant="body2" data-testid="med-national-tracking-code">
              {t('med:national_referral.tracking_code')}: <bdi dir="ltr">{data.trackingCode}</bdi>
              {data.registeredAt ? ` · ${toJalaliDateTime(data.registeredAt, { timeZone })}` : ''}
            </Typography>
          ) : (
            <Alert severity="info" data-testid="med-national-not-registered">
              {t('med:national_referral.not_registered')}
            </Alert>
          )}
          {data.feedbackSummary ? (
            <Alert severity="success" data-testid="med-national-feedback">
              <Typography variant="body2">{t('med:national_referral.feedback')}: {data.feedbackSummary}</Typography>
              {data.feedbackOn ? <Typography variant="caption">{t('med:national_referral.feedback_on')}: {data.feedbackOn}</Typography> : null}
            </Alert>
          ) : data.trackingCode && data.feedbackAt ? (
            <Alert severity="info" data-testid="med-national-no-feedback">
              {t('med:national_referral.no_feedback')}
            </Alert>
          ) : null}
          {data.error ? (
            <Alert severity="warning" data-testid="med-national-error">
              {t('med:national_referral.error')}
            </Alert>
          ) : null}
          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {!data.trackingCode && (
                <Button size="small" onClick={() => register.mutate()} disabled={register.isPending} data-testid="med-national-register">
                  {t('med:national_referral.register')}
                </Button>
              )}
              {data.trackingCode && (
                <Button size="small" onClick={() => feedback.mutate()} disabled={feedback.isPending} data-testid="med-national-fetch-feedback">
                  {t('med:national_referral.fetch_feedback')}
                </Button>
              )}
            </Stack>
          )}
          {register.error ? <ErrorState error={register.error} compact /> : null}
          {feedback.error ? <ErrorState error={feedback.error} compact /> : null}
        </Stack>
      )}
    </Stack>
  );
}

function ReferralDetailDialog({ id, canWrite, onClose }: { id: string; canWrite: boolean; onClose: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const query = useQuery({ queryKey: medKeys.referral(session?.tenantId, id), queryFn: () => unwrap(tenantApi.GET('/api/v1/medical-orders/referrals/{id}', { params: { path: { id } } })) });
  const data = query.data;
  return (
    <FormDialog open titleKey="med:referral.detail" submitKey="med:common.close" onSubmit={onClose} onClose={onClose} isDirty={false} isSubmitting={false}>
      {query.isPending ? (
        <LoadingState rows={3} />
      ) : query.error || !data ? (
        <ErrorState error={query.error} compact />
      ) : (
        <Stack spacing={1} data-testid="med-referral-detail">
          <Typography variant="body2">
            {t('med:referral.reason')}: {data.reason}
          </Typography>
          {data.clinicalQuestion && (
            <Typography variant="body2">
              {t('med:referral.question')}: {data.clinicalQuestion}
            </Typography>
          )}
          {data.declineReason && <Alert severity="warning">{t('med:referral.declined_with', { reason: data.declineReason })}</Alert>}
          {data.response && (
            <Alert severity="info" data-testid="med-referral-response">
              <Typography variant="subtitle2">{t('med:referral.response_from', { name: data.response.respondedByName ?? '', at: toJalaliDateTime(data.response.respondedAt, { timeZone }) })}</Typography>
              <Typography variant="body2">{t('med:referral.findings')}: {data.response.findings}</Typography>
              {data.response.diagnosis && <Typography variant="body2">{t('med:referral.diagnosis')}: {data.response.diagnosis}</Typography>}
              {data.response.actionsTaken && <Typography variant="body2">{t('med:referral.actions_taken')}: {data.response.actionsTaken}</Typography>}
              {data.response.recommendations && <Typography variant="body2">{t('med:referral.recommendations')}: {data.response.recommendations}</Typography>}
              {data.response.followUpNeeded && <Chip size="small" color="warning" label={t('med:referral.follow_up_needed')} />}
            </Alert>
          )}
          <Divider />
          <NationalReferralPanel id={id} canWrite={canWrite} />
        </Stack>
      )}
    </FormDialog>
  );
}

function RespondDialog({ id, onClose, onDone }: { id: string; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation('med');
  const { tenantApi } = useTenantSession();
  const [form, setForm] = useState({ findings: '', diagnosis: '', actionsTaken: '', recommendations: '' });
  const [followUp, setFollowUp] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/referrals/{id}:respond', {
          params: { path: { id } },
          body: { findings: form.findings.trim(), diagnosis: form.diagnosis.trim() || null, actionsTaken: form.actionsTaken.trim() || null, recommendations: form.recommendations.trim() || null, followUpNeeded: followUp, noteId: null, encounterId: null },
        }),
      ),
    onSuccess: onDone,
  });
  const field = (name: keyof typeof form, multiline = false) => (
    <TextField label={t(`med:referral.${name === 'actionsTaken' ? 'actions_taken' : name}`)} value={form[name]} required={name === 'findings'} multiline={multiline} minRows={multiline ? 2 : undefined} onChange={(event) => setForm((current) => ({ ...current, [name]: event.target.value }))} slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': `med-respond-${name}` } }} />
  );
  return (
    <FormDialog open titleKey="med:referral.respond" submitKey="med:referral.send_response" onSubmit={() => (form.findings.trim() ? save.mutate() : undefined)} onClose={onClose} isDirty={form.findings.trim() !== ''} isSubmitting={save.isPending} submitError={save.error}>
      {field('findings', true)}
      {field('diagnosis')}
      {field('actionsTaken', true)}
      {field('recommendations', true)}
      <FormControlLabel control={<Checkbox checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} />} label={t('med:referral.follow_up_needed')} />
    </FormDialog>
  );
}

export function ReferralsPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { timeZone } = useDisplaySettings();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const canRespond = access.can('med.referral.respond');
  const canManage = access.can('med.referral.schedule');
  // AP-CLN-04 (US-MED-064): تب «ارجاع ورودی از بیرون» برای پذیرش.
  const canTrack = access.can('med.national_referral.track');
  const boxes: Box[] = [
    ...(canRespond ? (['incoming', 'outgoing'] as const) : []),
    ...(canManage ? (['tracking', 'inbound'] as const) : []),
    ...(canTrack ? (['codes'] as const) : []),
  ];
  const [box, setBox] = useState<Box>(boxes[0] ?? 'tracking');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [detail, setDetail] = useState<string | null>(null);
  const [declining, setDeclining] = useState<string | null>(null);
  const [responding, setResponding] = useState<string | null>(null);
  const [booking, setBooking] = useState<MedReferralListItem | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const list = useQuery({
    queryKey: [...medKeys.referrals(tenantId, box), overdueOnly],
    enabled: box !== 'inbound' && box !== 'codes',
    queryFn: () =>
      unwrap(
        box === 'tracking'
          ? tenantApi.GET('/api/v1/medical-orders/referrals/tracking', { params: { query: { overdue: overdueOnly || undefined } } })
          : tenantApi.GET('/api/v1/medical-orders/referrals', { params: { query: { box, overdue: overdueOnly || undefined } } }),
      ),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['medorders', tenantId, 'referrals'] });
  const prefix = box === 'tracking' ? '/api/v1/medical-orders/referrals/tracking/{id}' : '/api/v1/medical-orders/referrals/{id}';
  const act = useMutation({
    mutationFn: async ({ id, action, reason, appointmentId }: { id: string; action: 'accept' | 'decline' | 'close' | 'cancel' | 'schedule'; reason?: string; appointmentId?: string }) => {
      const params = { params: { path: { id } } };
      switch (action) {
        case 'accept':
          return unwrap(tenantApi.POST(`${prefix}:accept` as '/api/v1/medical-orders/referrals/{id}:accept', params));
        case 'decline':
          return unwrap(tenantApi.POST('/api/v1/medical-orders/referrals/{id}:decline', { ...params, body: { reason: reason ?? '' } }));
        case 'close':
          return unwrap(tenantApi.POST(`${prefix}:close` as '/api/v1/medical-orders/referrals/{id}:close', params));
        case 'cancel':
          return unwrap(tenantApi.POST('/api/v1/medical-orders/referrals/{id}:cancel', params));
        case 'schedule':
          return unwrap(tenantApi.POST(`${prefix}:schedule` as '/api/v1/medical-orders/referrals/{id}:schedule', { ...params, body: { appointmentId: appointmentId ?? '' } }));
      }
    },
    onSuccess: (_data, variables) => {
      setDeclining(null);
      setNotice(t(`med:referral.done.${variables.action}`));
      refresh();
    },
  });
  const columns: DataTableColumn<MedReferralListItem>[] = [
    { id: 'patient', headerKey: 'med:common.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'from', headerKey: 'med:referral.from', cell: (row) => row.fromProviderName ?? '—' },
    { id: 'to', headerKey: 'med:referral.to', cell: (row) => row.toProviderName ?? (row.toSpecialty ? t(`med:specialty.${row.toSpecialty}`, { defaultValue: row.toSpecialty }) : t(`med:target_kind.${row.targetKind}`)) },
    { id: 'urgency', headerKey: 'med:referral.urgency', cell: (row) => (row.urgency === 'Urgent' ? <Chip size="small" color="warning" label={t('med:priority.Urgent')} /> : t('med:priority.Routine')) },
    { id: 'status', headerKey: 'med:common.status', cell: (row) => <Stack direction="row" spacing={0.5}><ReferralStatusChip status={row.status} />{row.overdue ? <Chip size="small" color="error" label={t('med:referral.overdue')} data-testid="med-referral-overdue" /> : null}</Stack> },
    { id: 'waiting', headerKey: 'med:referral.waiting', cell: (row) => t('med:referral.hours', { hours: Number(row.waitingHours) }) },
    { id: 'created', headerKey: 'med:common.date', cell: (row) => toJalaliDateTime(row.createdAt, { timeZone }) },
    {
      id: 'actions',
      headerKey: 'med:common.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {box !== 'tracking' && (
            <Button size="small" onClick={() => setDetail(row.id)} data-testid="med-referral-open">
              {t('med:common.view')}
            </Button>
          )}
          {full && box !== 'outgoing' && row.status === 'Sent' && (
            <Button size="small" onClick={() => act.mutate({ id: row.id, action: 'accept' })} data-testid="med-referral-accept">
              {t('med:referral.accept')}
            </Button>
          )}
          {full && box === 'incoming' && row.status === 'Sent' && (
            <Button size="small" color="error" onClick={() => setDeclining(row.id)} data-testid="med-referral-decline">
              {t('med:referral.decline')}
            </Button>
          )}
          {full && box !== 'outgoing' && (row.status === 'Accepted' || row.status === 'Scheduled') && (
            <Button size="small" onClick={() => setBooking(row)} data-testid="med-referral-book">
              {t('med:referral.book')}
            </Button>
          )}
          {full && box === 'incoming' && (row.status === 'Accepted' || row.status === 'Scheduled') && (
            <Button size="small" onClick={() => setResponding(row.id)} data-testid="med-referral-respond">
              {t('med:referral.respond')}
            </Button>
          )}
          {full && box !== 'incoming' && (row.status === 'Responded' || row.status === 'Declined') && (
            <Button size="small" onClick={() => act.mutate({ id: row.id, action: 'close' })} data-testid="med-referral-close">
              {t('med:referral.close')}
            </Button>
          )}
          {full && box === 'outgoing' && row.status === 'Sent' && (
            <Button size="small" color="inherit" onClick={() => act.mutate({ id: row.id, action: 'cancel' })} data-testid="med-referral-cancel">
              {t('med:referral.cancel')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_705}
      titleKey="med:referrals_page.title"
      subtitleKey="med:referrals_page.subtitle"
      filters={<FormControlLabel control={<Checkbox checked={overdueOnly} onChange={(event) => setOverdueOnly(event.target.checked)} />} label={t('med:referral.overdue_only')} data-testid="med-referral-overdue-only" />}
    >
      <Tabs value={box} onChange={(_, next: Box) => setBox(next)} sx={{ mb: 1.5 }} aria-label={t('med:referrals_page.title')}>
        {boxes.map((item) => (
          <Tab key={item} value={item} label={t(`med:referrals_page.${item}`)} data-testid={`med-referrals-tab-${item}`} />
        ))}
      </Tabs>
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 1 }} data-testid="med-referral-notice">
          {notice}
        </Alert>
      )}
      {act.error ? <ErrorState error={act.error} compact /> : null}
      {box === 'inbound' ? <InboundReferralsPanel patientId={null} /> : null}
      {box === 'codes' ? <TrackingCodeSearchPanel /> : null}
      {box !== 'inbound' && box !== 'codes' && <DataTable captionKey={`med:referrals_page.${box}`} columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="med:referrals_page.empty" />}
      {detail && <ReferralDetailDialog id={detail} canWrite={full && access.can('med.referral.create')} onClose={() => setDetail(null)} />}
      {responding && (
        <RespondDialog
          id={responding}
          onClose={() => setResponding(null)}
          onDone={() => {
            setResponding(null);
            setNotice(t('med:referral.done.respond'));
            refresh();
          }}
        />
      )}
      <ReasonDialog open={declining !== null} titleKey="med:referral.decline" messageKey="med:referral.decline_message" busy={act.isPending} onConfirm={(reason) => declining && act.mutate({ id: declining, action: 'decline', reason })} onCancel={() => setDeclining(null)} />
      {booking && (
        <BookingDialog
          open
          mode="book"
          prefill={{ patient: { id: booking.patientId, displayName: booking.patientName ?? '' } }}
          onClose={() => setBooking(null)}
          onDone={(result) => {
            const id = booking.id;
            setBooking(null);
            act.mutate({ id, action: 'schedule', appointmentId: result.appointmentId });
          }}
        />
      )}
    </ListPage>
  );
}
